import { useCallback, useEffect } from 'react'

import type { GetStaticPropsContext } from 'next'
import { useSession } from 'next-auth/react'
import { NextSeo } from 'next-seo'
import { useRouter } from 'next/router'

import { getAllPathsByType, getDocumentByUID, sliceFetchLinks } from '@lib/prismic/fetch/documents'
import sliceResolvers from '@lib/prismic/helpers/sliceResolvers'

import AllSlices from '@components/prismic/AllSlices'

const tagsToPages: { [key: string]: { value: string; redirect: string } } = {
	'ace-membership': {
		value: 'ACE',
		redirect: '/ace-membership-inaccessible'
	}
}

export default function Page({ uid, page }: { uid: string; page: any }) {
	const { replace } = useRouter()
	const { data } = useSession()

	const redirectUserIfCantAccessThisPage = useCallback(
		(slug: string, customer: { tags: string[] }) => {
			if (tagsToPages[slug] && (!customer || !customer.tags.includes(tagsToPages[slug].value))) {
				replace(tagsToPages[slug].redirect)
			}
		},
		[replace]
	)

	useEffect(() => {
		if (data !== undefined) redirectUserIfCantAccessThisPage(uid, data?.user.customer)
	}, [data, uid, redirectUserIfCantAccessThisPage])

	return (
		<>
			<NextSeo title={page?.title?.[0]?.text ? page.title[0].text : null} />
			<AllSlices body={page?.body} />
		</>
	)
}

type GetStaticPropsParams = {
	uid: string
}

export async function getStaticProps({
	params,
	previewData
}: GetStaticPropsContext<GetStaticPropsParams>) {
	const { uid } = params as GetStaticPropsParams

	const page =
		(await getDocumentByUID(
			'page',
			uid,
			{
				// TODO: This depends on the content on the slices; @TJ feel free to add linked properties below
				fetchLinks: sliceFetchLinks
			},
			previewData
		)) ??
		(await getDocumentByUID('page_rebrand', uid, { fetchLinks: sliceFetchLinks }, previewData))

	if (!page) {
		return {
			notFound: true
		}
	}

	// Only resolve slices for types that have a body
	if (page.type === 'page' || page.type === 'page_rebrand') {
		page.data.body = (await sliceResolvers(page.data.body)) as typeof page.data.body
	}

	return {
		props: {
			uid,
			page: page.data
		}
	}
}

export async function getStaticPaths({ previewData }: { previewData: any }) {
	const pagePaths = await getAllPathsByType('page', previewData)
	const pageRebrandPaths = await getAllPathsByType('page_rebrand', previewData)

	return {
		paths: [...pagePaths, ...pageRebrandPaths],
		fallback: false
	}
}
